import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Palette, Type, Calendar, Sparkles, Image as ImageIcon, Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import DecorationEditor from '../../components/DecorationEditor';
import BackgroundSelector from '../../components/BackgroundSelector';

const SeasonalThemeEditor = () => {
  const navigate = useNavigate();
  const { themeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [themeData, setThemeData] = useState({
    id: themeId || 'custom',
    name: '',
    description: '',
    icon: '⚙️',
    season: '',
    period: '',
    marketingText: '',
    colors: {
      primary: '#C4A084',
      secondary: '#B89968',
      background: '#E8E0D5',
      accent: '#6D5A47',
      accentSecondary: '#8B7355'
    },
    decorations: [],
    backgroundSections: {
      global: { id: 'solid-beige', type: 'solid', value: '#E8E0D5', name: 'Beige' },
      hero: null,
      products: null,
      newsletter: null,
      footer: null
    }
  });

  // Load theme data if editing existing theme
  useEffect(() => {
    if (themeId && themeId !== 'new') {
      loadThemeData();
    }
  }, [themeId]);

  const loadThemeData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/seasonal/${themeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setThemeData(data);
      }
    } catch (error) {
      console.error('Error loading theme:', error);
      toast.error('Fehler beim Laden des Themes');
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (colorKey, value) => {
    setThemeData(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: value
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setThemeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/seasonal/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(themeData)
      });

      if (!response.ok) {
        throw new Error('Failed to save theme');
      }

      toast.success('Theme erfolgreich gespeichert! 🎉');
      setTimeout(() => navigate('/admin/seasonal-themes'), 1000);
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.error('Fehler beim Speichern des Themes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="text-center">
          <Sparkles className="h-12 w-12 text-purple-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Lade Theme...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/seasonal-themes')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Seasonal Themes
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Palette className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {themeId === 'new' ? 'Neues Theme erstellen' : 'Theme bearbeiten'}
                </h1>
                <p className="text-gray-600">Passen Sie Farben, Texte und Dekorationen an</p>
              </div>
            </div>
            
            <Button onClick={handleSave} disabled={saving} size="lg">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="h-5 w-5" />
                  Grundinformationen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Theme-Name *</Label>
                    <Input
                      id="name"
                      value={themeData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="z.B. Weihnachten"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="icon">Emoji-Icon</Label>
                    <Input
                      id="icon"
                      value={themeData.icon}
                      onChange={(e) => handleInputChange('icon', e.target.value)}
                      placeholder="🎄"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Beschreibung</Label>
                  <Input
                    id="description"
                    value={themeData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Kurze Beschreibung des Themes"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="season">Saison</Label>
                    <Input
                      id="season"
                      value={themeData.season}
                      onChange={(e) => handleInputChange('season', e.target.value)}
                      placeholder="z.B. Winter"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="period">
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Zeitraum
                    </Label>
                    <Input
                      id="period"
                      value={themeData.period}
                      onChange={(e) => handleInputChange('period', e.target.value)}
                      placeholder="z.B. November - Dezember"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="marketingText">Marketing-Text</Label>
                  <Textarea
                    id="marketingText"
                    value={themeData.marketingText}
                    onChange={(e) => handleInputChange('marketingText', e.target.value)}
                    placeholder="Text für Ihre Kunden..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Color Palette */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Farbpalette
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(themeData.colors).map(([key, value]) => (
                    <div key={key}>
                      <Label htmlFor={key} className="capitalize">
                        {key === 'primary' ? 'Primär' :
                         key === 'secondary' ? 'Sekundär' :
                         key === 'background' ? 'Hintergrund' :
                         key === 'accent' ? 'Akzent 1' :
                         key === 'accentSecondary' ? 'Akzent 2' : key}
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          id={key}
                          value={value}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          className="h-10 w-16 rounded border border-gray-300 cursor-pointer"
                        />
                        <Input
                          value={value}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          placeholder="#000000"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Background Sections */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Hintergründe & Abschnitte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BackgroundSelector
                  sections={themeData.backgroundSections || {}}
                  onSectionsChange={(sections) => handleInputChange('backgroundSections', sections)}
                />
              </CardContent>
            </Card>

            {/* Decoration Library & Editor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Dekorationen
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">✓ Verfügbar</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DecorationEditor
                  placedDecorations={themeData.decorations || []}
                  onDecorationsChange={(decorations) => handleInputChange('decorations', decorations)}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Live Preview */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Live-Vorschau</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="rounded-lg p-6 min-h-[400px]"
                  style={{ 
                    backgroundColor: themeData.colors.background,
                    border: `2px solid ${themeData.colors.primary}`
                  }}
                >
                  {/* Preview Header */}
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-2">{themeData.icon}</div>
                    <h3 className="text-xl font-bold" style={{ color: themeData.colors.primary }}>
                      {themeData.name || 'Theme-Name'}
                    </h3>
                    {themeData.season && (
                      <p className="text-sm text-gray-600">{themeData.season} • {themeData.period}</p>
                    )}
                  </div>

                  {/* Preview Colors */}
                  <div className="space-y-3">
                    <div
                      className="rounded px-4 py-3 text-white text-sm font-medium"
                      style={{ backgroundColor: themeData.colors.primary }}
                    >
                      Primär-Farbe
                    </div>
                    <div
                      className="rounded px-4 py-3 text-white text-sm font-medium"
                      style={{ backgroundColor: themeData.colors.secondary }}
                    >
                      Sekundär-Farbe
                    </div>
                    <div
                      className="rounded px-4 py-3 text-white text-sm font-medium"
                      style={{ backgroundColor: themeData.colors.accent }}
                    >
                      Akzent 1
                    </div>
                    {themeData.colors.accentSecondary && (
                      <div
                        className="rounded px-4 py-3 text-white text-sm font-medium"
                        style={{ backgroundColor: themeData.colors.accentSecondary }}
                      >
                        Akzent 2
                      </div>
                    )}
                  </div>

                  {/* Preview Marketing Text */}
                  {themeData.marketingText && (
                    <div className="mt-6 p-4 bg-white/50 rounded text-sm text-gray-700">
                      {themeData.marketingText}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalThemeEditor;
